import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';

/**
 * Calculates the digit sum of a given number
 */
function getDigitSum(num: number) {
  return String(num)
    .split('')
    .reduce((acc, curr) => {
      return acc + Number(curr);
    }, 0);
}

/**
 * Calculated following the instructions at http://www.pruefziffernberechnung.de/V/VSNR-DE.shtml
 * Example valid numbers: 65 180539 W 001, 15 070649 C 103
 * Confirmed by https://www.lohnservice.com/RVNrtest1,02.htm
 * @param rvnr
 * @returns
 */
function validRVNR(rvnr: string) {
  // Allow space, minus(-) and slash (/) as number separators
  rvnr = rvnr.trim().replace(/\s|\/|\-/g, '');

  // 12 digits
  if (!(rvnr.length === 12)) {
    return false;
  }

  /*
   * The first two digits have to be numbers. They represent the insurance.
   * The next six digits represent the birthday, we don't further check if it is a valid date because in rare cases too many people
   * share the same starting first name letter and birthday, which causes the date in their RVNR to be adjusted to an invalid date.
   */
  if (!/^[0-9]{8}$/.test(rvnr.slice(0, 8))) {
    return false;
  }

  // Digit nine is the first letter of the last name.
  if (!rvnr[8] || !/^[A-Za-z]$/.test(rvnr[8])) {
    return false;
  }

  // Digits ten and eleven have to be numbers. They represent the gender.
  if (!/^[0-9]{2}$/.test(rvnr.slice(9, 11))) {
    return false;
  }

  /*
   * Last digit is the check digit. It is calculated as follows:
   *  1. digit nine is converted to a number between 1 and 26
   *  2. the digits are multiplied by 2, 1, 2, 5, 7, 1, 2, 1, 2, 1, 2 and 1, respectively
   *  3. the digit sums of the products is calculated
   *  4. all digit sums are added
   *  5. the rightmost digit of the final sum is the check digit (final sum % 10)
   */
  const factors = [2, 1, 2, 5, 7, 1, 2, 1, 2, 1, 2, 1];
  // Digit nine conversion
  const charNineConverted = (rvnr[8].toLowerCase().charCodeAt(0) - 96).toLocaleString('de-DE', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  });

  const testString = rvnr.slice(0, 8) + charNineConverted + rvnr.slice(9, 11);

  let digitSums = 0;
  factors.forEach((value, index) => {
    digitSums += getDigitSum(Number(testString[index]) * value);
  });
  return Number(rvnr[11]) === digitSums % 10;
}

export function validateRVNR(v: ValidationFunctionOptions<string, any>) {
  if (!validRVNR(v.value))
    throw new ValidationError('Rentenversicherungsnummer ist nicht gültig', {
      key: 'rentenVersicherungsNr',
      translationKey: 'validation_rentenVersicherungsNr',
      path: v.path,
    });
}
